/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { AfterViewInit, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { CommunicationType } from '@gh/builder-shared-data';
import {
  createDialogRoute,
  FormState,
  FormStateBuilder,
  TextTypes,
  whenCreateOrEditMode,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { DEFAULT_COUNTRY_CODE } from '@gh/config';
import {
  AutocompleteLoadMoreEvent,
  DialogInput,
  DialogService,
  FormMode,
  NotificationService,
  responsiveDialogOverlayPaneClass,
  WaitingService,
  yesAnswer,
  yesNoConfirmation,
} from '@gh/core-ui';
import { filter$ } from '@gh/rx/operators';
import {
  CommunicationsBusinessService,
  SmsDialogConfig,
} from '../../resources/communications-business.service';
import { CommunicationListItem, SmsItem } from '../../resources/communications.model';
import { SmsService } from '../../../services/sms.service';

const MESSAGE_MIN_ROWS = 3;
const MESSAGE_MAX_ROWS = 10;
const MESSAGE_ROW_HEIGHT_PX = 24;

@Component({
  standalone: false,
  selector: 'gh-sms-dialog',
  templateUrl: 'sms-dialog.component.html',
  styleUrls: ['sms-dialog.component.scss'],
})
export class SmsDialogComponent implements OnInit, AfterViewInit, DialogInput<SmsDialogConfig> {
  input: SmsDialogConfig;
  form: FormState<SmsItem>;
  communicationListItem: CommunicationListItem;
  otherContactNumbers: string[] = [];

  @ViewChild('messageInput') messageInput: ElementRef<HTMLTextAreaElement> | null = null;

  constructor(private dialogRef: MatDialogRef<any>,
              private formStateBuilder: FormStateBuilder,
              private businessService: CommunicationsBusinessService,
              private waitingService: WaitingService,
              private smsService: SmsService,
              private notificationService: NotificationService,
              private dialogService: DialogService) {

  }

  ngOnInit(): void {
    const sms = this.input.sms;
    if (sms && sms.communicationId && !sms.isMarkedAsRead) {
      this.businessService.readSms(sms.communicationId!).pipe(
        this.waitingService.operator(),
      ).subscribe((response) => {
        this.smsService.sendName();
        this.communicationListItem = response;
      });
    }
    const formMode = sms && sms.communicationId
    && (!this.input.mode || this.input.mode === FormMode.ViewMode) ? FormMode.ViewMode : FormMode.CreateMode;
    // add country code only if it does not exist in the phone
    const phone = formMode === FormMode.CreateMode
      ? this.formatPhoneWithCountryCode(sms?.cellPhone)
      : sms?.cellPhone;

    this.form = this.formStateBuilder.buildPlain({
      value: {
        ...sms,
        cellPhone: phone,
      },
      mode: formMode,
      enabled: whenCreateOrEditMode(),
      children: {
        cellPhone: { required: true, type: TextTypes.Phone },
        template: void 0,
        message: { required: true },
        jobId: void 0,
      },
    });
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.resizeMessageField(), 0);
  }

  resizeMessageField(): void {
    const el = this.messageInput?.nativeElement;
    if (!el) return;
    const value = this.form?.group?.get('message')?.value ?? '';
    const hasValue = typeof value === 'string' && value.trim().length > 0;
    el.style.height = 'auto';
    if (!hasValue) {
      el.style.height = '40px';
      return;
    }
    const minH = MESSAGE_ROW_HEIGHT_PX * MESSAGE_MIN_ROWS;
    const maxH = MESSAGE_ROW_HEIGHT_PX * MESSAGE_MAX_ROWS;
    const h = Math.min(maxH, Math.max(minH, el.scrollHeight));
    el.style.height = `${h}px`;
  }

  refreshOtherPhones(): void {
    const currentPrimary = this.form.value.cellPhone;
    const cellPhone = this.input.sms?.cellPhone;
    const otherPhone = this.input.sms?.otherPhone;
    if (!otherPhone) {
      this.otherContactNumbers = [];
      return;
    }
    const formattedPrimary = this.formatPhoneWithCountryCode(cellPhone);
    const formattedOther = this.formatPhoneWithCountryCode(otherPhone);

    if (currentPrimary && currentPrimary === formattedOther) {
      this.otherContactNumbers = [formattedPrimary];
    } else if (!currentPrimary) {
      this.otherContactNumbers = [formattedPrimary, formattedOther];
    } else if (currentPrimary) {
      this.otherContactNumbers = [formattedPrimary, formattedOther].filter(phone => phone !== currentPrimary);
    } else {
      this.otherContactNumbers = [];
    }
  }

  private formatPhoneWithCountryCode(phone?: string): string {
    if (!phone) return `+${DEFAULT_COUNTRY_CODE}`;
    const cleaned = phone.replace(/\D/g, '');
    return phone.startsWith('+')
      ? phone
      : `+${DEFAULT_COUNTRY_CODE}${cleaned}`;
  }

  loadSmsTemplates($event: AutocompleteLoadMoreEvent): void {
    const { load } = $event;

    load(this.businessService.getSmsTemplates());
  }

  onPhoneSelected(event: any): void {
    const selectedPhone = event.option?.value || event;
    if (!selectedPhone) {
      return;
    }
    this.form.setValue({
      ...this.form.value,
      cellPhone: selectedPhone,
    });
  }

  onSelectTemplate(template: any): void {
    if (!template) {
      return;
    }
    this.businessService.getFilledSmsTemplate(template?.id, this.input?.contactId, this.input?.jobId).subscribe((res) => {
      this.form.build({
        cellPhone: this.form.value.cellPhone,
        message: res.smsTemplateDto.body,
        jobId: this.form.value.jobId,
        template: this.form.value.template,
      });
      setTimeout(() => this.resizeMessageField(), 0);

      if (res.unresolvedPlaceholders.length) {
        const unresolved = res.unresolvedPlaceholders.join(', ');
        this.notificationService.warning(`There are no values for ${unresolved}`);
      }
    });
  }

  onCancel(): void {
    if (this.form.group.dirty) {
      this.dialogService
        .confirmation(yesNoConfirmation('msg_changes_not_saved')).pipe(
        filter$(yesAnswer))
        .subscribe(() => this.dialogRef.close());
    } else {
      this.dialogRef.close();
    }
  }

  onOk(): void {
    this.dialogRef.close(this.communicationListItem);
  }

  onSend(): void {
    if (this.form.group.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const { sms, contactId } = this.input;

    this.businessService.sendSms(this.input.entryPoint, this.input, {
      ...{
        cellPhone: this.form.value.cellPhone,
        jobId: this.form.value.jobId,
        message: this.form.value.message,
      },
      communicationType: CommunicationType.OutgoingSms,
      contactId: <ID>(sms && sms.contactId || contactId),
      isMarkedAsRead: true,
      contactTypeCode: this.input.sms!.contactTypeCode,
    }).pipe(
      this.waitingService.operator<CommunicationListItem>())
      .subscribe(
        (communicationListItem: CommunicationListItem) => {
          this.notificationService.success('msg_sms_sent_successfully');
          this.dialogRef.close(communicationListItem);
        },      (error) => {
          if (error && error.messages[0]?.key === 'msg_invalid_phone_number') {
            this.notificationService.error('msg_invalid_phone_number', { countryCode: `+${DEFAULT_COUNTRY_CODE}`});
            return;
          }
          if (error && error.messages[0]?.key === 'msg_inactive_sms_for_office') {
            this.notificationService.error('msg_inactive_sms_for_office');
            return;
          }
          this.notificationService.processAll(error);
        });
  }
}

export const smsDialogRoute = createDialogRoute({
  component: SmsDialogComponent,
  config: { disableClose: true, panelClass: responsiveDialogOverlayPaneClass() },
});
